const ALLOWED_TAGS = new Set([
  "P",
  "BR",
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "H2",
  "H3",
  "H4",
  "UL",
  "OL",
  "LI",
  "BLOCKQUOTE",
  "A",
  "IMG",
  "TABLE",
  "THEAD",
  "TBODY",
  "TFOOT",
  "TR",
  "TH",
  "TD",
  "CAPTION",
]);

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function hasHtmlMarkup(value: string) {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function formatPlainTextAsHtml(value: string) {
  return value
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

export function sanitizeRichTextHtml(value: string) {
  const html = hasHtmlMarkup(value) ? value : formatPlainTextAsHtml(value);

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return html;
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(`<div>${html}</div>`, "text/html");
  const root = documentFragment.body.firstElementChild as HTMLElement | null;

  if (!root) {
    return "";
  }

  const sanitizeNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tagName = element.tagName.toUpperCase();

      if (!ALLOWED_TAGS.has(tagName)) {
        if (tagName === "SCRIPT" || tagName === "STYLE") {
          element.remove();
          return;
        }

        const parent = element.parentNode;
        while (element.firstChild) {
          parent?.insertBefore(element.firstChild, element);
        }
        parent?.removeChild(element);
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value.trim();

        if (tagName === "A") {
          if (name === "href") {
            const isSafeHref =
              value.startsWith("http://") ||
              value.startsWith("https://") ||
              value.startsWith("mailto:") ||
              value.startsWith("tel:") ||
              value.startsWith("/");

            if (!isSafeHref) {
              element.removeAttribute(attribute.name);
            }
            return;
          }

          if (name === "target") {
            element.setAttribute("target", "_blank");
            element.setAttribute("rel", "noopener noreferrer");
            return;
          }

          if (name !== "rel" && name !== "title") {
            element.removeAttribute(attribute.name);
          }
          return;
        }

        if (tagName === "IMG") {
          if (name === "src") {
            const isSafeSrc =
              value.startsWith("http://") ||
              value.startsWith("https://") ||
              value.startsWith("data:image/") ||
              value.startsWith("/");
            if (!isSafeSrc) {
              element.remove();
            }
            return;
          }

          if (!["alt", "title", "width", "height"].includes(name)) {
            element.removeAttribute(attribute.name);
          }
          return;
        }

        if (tagName === "TH" || tagName === "TD") {
          if (name === "colspan" || name === "rowspan") {
            const parsedValue = Number.parseInt(value, 10);
            if (!Number.isFinite(parsedValue) || parsedValue < 1 || parsedValue > 20) {
              element.removeAttribute(attribute.name);
            } else {
              element.setAttribute(attribute.name, String(parsedValue));
            }
            return;
          }

          if (tagName === "TH" && name === "scope" && ["col", "row", "colgroup", "rowgroup"].includes(value)) {
            return;
          }

          element.removeAttribute(attribute.name);
          return;
        }

        element.removeAttribute(attribute.name);
      });
    }

    Array.from(node.childNodes).forEach(sanitizeNode);
  };

  Array.from(root.childNodes).forEach(sanitizeNode);
  return root.innerHTML.trim();
}

export function stripRichTextToPlainText(value: string) {
  const html = sanitizeRichTextHtml(value);

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }

  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(`<div>${html}</div>`, "text/html");
  return documentFragment.body.textContent?.replace(/\s+/g, " ").trim() || "";
}
